import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Merge, ShieldCheck, RefreshCw, AlertTriangle, ArrowRight, Loader2, Search, Link2, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const _API_BASE = (typeof window !== "undefined" && window.location.hostname !== "localhost") ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : ""));
const API = `${_API_BASE}/api`;

/**
 * Data Hygiene admin page.
 *
 * Surfaces suspected duplicate clients from ``suggest_duplicates()``
 * so an admin can merge them into a single canonical record. Also shows
 * a live resolver ("what does REX think 'CBN' is?") and the recent
 * audit trail of merges / alias adds.
 *
 * Requires admin or owner role (backend enforces via _require_admin).
 */
export default function DataHygienePage() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedKeys, setResolvedKeys] = useState(() => new Set());
  const [resolvedCount, setResolvedCount] = useState(0);
  const [reasonFor, setReasonFor] = useState(null);   // pair key whose reason form is open
  const [reasonText, setReasonText] = useState("");
  const [relatedFor, setRelatedFor] = useState(null); // pair key whose "related" form is open
  const [relText, setRelText] = useState("");
  const [relNote, setRelNote] = useState("");
  const [mergeConfirm, setMergeConfirm] = useState(null); // { keeperId, sourceId, keeper, source, pair, key }
  const [merging, setMerging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [audit, setAudit] = useState([]);
  const [resolveRef, setResolveRef] = useState("");
  const [resolveResult, setResolveResult] = useState(null);
  const [resolving, setResolving] = useState(false);

  const loadPairs = async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/admin/data-hygiene/duplicate-clients`, { credentials: "include" });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || `Failed (${r.status})`);
      }
      const d = await r.json();
      setPairs(d.pairs || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadAudit = async () => {
    try {
      const r = await fetch(`${API}/admin/data-hygiene/client-audit?limit=30`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      setAudit(d.events || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadPairs(); loadAudit(); }, []);

  // Part 6 — honest confirmation: name both, say which survives, irreversible,
  // and flag a country mismatch (the strongest "country arm, not duplicate" signal).
  const askMerge = (keeper, source, key) => setMergeConfirm({ keeper, source, key });
  const doMerge = async () => {
    if (!mergeConfirm) return;
    const { keeper, source, key } = mergeConfirm;
    setMerging(true);
    try {
      const r = await fetch(`${API}/admin/data-hygiene/merge-clients`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keeper_id: keeper.client_id, source_id: source.client_id }),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b?.detail || "merge failed");
      const rows = Object.entries(b.rows_rewritten || {}).map(([k, v]) => `${v} ${k}`).join(", ") || "no FK rows";
      toast.success(`Merged. Rewrote ${rows}. Aliases added: ${(b.aliases_added || []).join(", ") || "none"}.`);
      setResolvedKeys(s => new Set(s).add(key));
      setResolvedCount(c => c + 1);
      setMergeConfirm(null);
      await loadPairs();
      await loadAudit();
    } catch (e) {
      toast.error(e.message || "merge failed");
    } finally {
      setMerging(false);
    }
  };

  const applyNotDuplicate = async (pair, key) => {
    const reason = reasonText.trim();
    if (!reason) { toast.error("A reason is required before this can be applied."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/data-hygiene/not-duplicate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id_a: pair.a.client_id, client_id_b: pair.b.client_id, reason }),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b?.detail || "could not record decision");
      toast.success(`Recorded. "${pair.a.company_name}" and "${pair.b.company_name}" won't be suggested again. Reason noted on both records.`);
      setResolvedKeys(s => new Set(s).add(key));
      setResolvedCount(c => c + 1);
      setReasonFor(null);
      setReasonText("");
      await loadAudit();
    } catch (e) {
      toast.error(e.message || "could not record decision");
    } finally {
      setSubmitting(false);
    }
  };

  const applyRelated = async (pair, key) => {
    const relationship = relText.trim();
    if (!relationship) { toast.error("Describe the relationship before applying."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/data-hygiene/related`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id_a: pair.a.client_id, client_id_b: pair.b.client_id, relationship, note: relNote.trim() }),
      });
      const b = await r.json();
      if (!r.ok) throw new Error(b?.detail || "could not record relationship");
      toast.success(`Recorded. "${pair.a.company_name}" and "${pair.b.company_name}" are related — written to both records.`);
      setResolvedKeys(s => new Set(s).add(key));
      setResolvedCount(c => c + 1);
      setRelatedFor(null);
      setRelText("");
      setRelNote("");
      await loadAudit();
    } catch (e) {
      toast.error(e.message || "could not record relationship");
    } finally {
      setSubmitting(false);
    }
  };

  const openReason = (key) => { setReasonFor(key); setReasonText(""); setRelatedFor(null); };
  const notNow = () => { setReasonFor(null); setReasonText(""); };  // leaves pair on the list, records nothing
  const openRelated = (key) => { setRelatedFor(key); setRelText(""); setRelNote(""); setReasonFor(null); };
  const relNotNow = () => { setRelatedFor(null); setRelText(""); setRelNote(""); };

  const resolve = async () => {
    if (!resolveRef.trim()) return;
    setResolving(true); setResolveResult(null);
    try {
      const r = await fetch(`${API}/admin/data-hygiene/resolve?reference=${encodeURIComponent(resolveRef.trim())}`, { credentials: "include" });
      const d = await r.json();
      setResolveResult(d);
    } catch (e) {
      toast.error(e.message || "resolve failed");
    } finally { setResolving(false); }
  };

  const visiblePairs = useMemo(() => pairs.filter(p => !resolvedKeys.has(`${p.a.client_id}:${p.b.client_id}`)), [pairs, resolvedKeys]);

  return (
    <div className="min-h-screen bg-slate-50 px-8 py-6" data-testid="data-hygiene-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Data Hygiene</h1>
            <p className="text-sm text-slate-600">Merge duplicate clients, add aliases, and check how REX resolves a reference.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { loadPairs(); loadAudit(); }} className="ml-auto" data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1"/> Refresh
          </Button>
        </header>

        {/* Resolver — live check of how REX interprets a name */}
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Search className="h-4 w-4 text-slate-600"/>
            <h2 className="font-semibold text-slate-900 text-sm">Resolve reference — "what client does REX think this is?"</h2>
          </div>
          <div className="flex gap-2">
            <Input
              value={resolveRef}
              onChange={(e) => setResolveRef(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && resolve()}
              placeholder="CBN, Central Bank, or a client name"
              className="flex-1"
              data-testid="resolver-input"
            />
            <Button onClick={resolve} disabled={resolving || !resolveRef.trim()} data-testid="resolver-btn">
              {resolving ? <Loader2 className="h-4 w-4 animate-spin"/> : "Resolve"}
            </Button>
          </div>
          {resolveResult && (
            <div className="mt-3 p-3 rounded border border-slate-200 bg-slate-50 text-sm" data-testid="resolver-result">
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge status={resolveResult.status}/>
                <span className="text-[11px] text-slate-500">confidence {(resolveResult.confidence * 100).toFixed(0)}%</span>
              </div>
              {resolveResult.client && (
                <p className="text-slate-900"><span className="font-semibold">{resolveResult.client.company_name}</span>
                  {resolveResult.client.acronym && <span className="ml-2 text-slate-500">({resolveResult.client.acronym})</span>}
                </p>
              )}
              {resolveResult.candidates?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[10px] text-slate-500 mb-1">Candidates:</p>
                  <ul className="space-y-1">
                    {resolveResult.candidates.map(c => (
                      <li key={c.client_id} className="text-xs flex items-center gap-2">
                        <span className="inline-block w-9 text-right tabular-nums">{(c.score * 100).toFixed(0)}%</span>
                        <span>{c.company_name}</span>
                        <span className="text-slate-400 text-[10px]">— {c.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Duplicate pairs */}
        <Card className="p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Merge className="h-4 w-4 text-slate-600"/>
              <h2 className="font-semibold text-slate-900 text-sm">Suspected duplicate clients</h2>
              <Badge variant="outline" className="text-[10px]" data-testid="dup-remaining-count">{visiblePairs.length} remaining</Badge>
              {resolvedCount > 0 && (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="dup-resolved-count">
                  {resolvedCount} resolved this session
                </Badge>
              )}
            </div>
          </div>
          {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5"/> {error}</p>}
          {loading && <p className="text-sm text-slate-500 flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin"/> Scanning clients…</p>}
          {!loading && !error && visiblePairs.length === 0 && (
            <p className="text-sm text-emerald-700 flex items-center gap-1" data-testid="dup-empty"><ShieldCheck className="h-3.5 w-3.5"/> No duplicates left to work. The list is clear.</p>
          )}
          <ul className="divide-y divide-slate-100">
            {visiblePairs.map((p, i) => {
              const key = `${p.a.client_id}:${p.b.client_id}`;
              const reasonOpen = reasonFor === key;
              const relatedOpen = relatedFor === key;
              const countryMismatch = p.a.country && p.b.country && p.a.country !== p.b.country;
              return (
                <li key={key} className="py-3" data-testid={`dup-pair-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-slate-900 truncate">{p.a.company_name}</span>
                        <Link2 className="h-3 w-3 text-slate-400"/>
                        <span className="font-semibold text-slate-900 truncate">{p.b.company_name}</span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${p.score >= 0.95 ? "bg-red-50 text-red-700 border-red-300" : p.score >= 0.9 ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-700 border-slate-300"}`}
                        >
                          {(p.score * 100).toFixed(0)}% match
                        </Badge>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">{p.reason}</p>
                      {countryMismatch && (
                        <p className="text-[11px] text-amber-700 mt-0.5" data-testid={`dup-country-flag-${i}`}>
                          ⚑ Different countries: {p.a.company_name} ({p.a.country}) vs {p.b.company_name} ({p.b.country}) — likely a country arm, not a duplicate.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => askMerge(p.a, p.b, key)} className="h-7 text-[11px]" data-testid={`merge-a-${i}`}>
                        Keep {truncate(p.a.company_name, 18)} <ArrowRight className="h-3 w-3 mx-1"/> Archive {truncate(p.b.company_name, 14)}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => askMerge(p.b, p.a, key)} className="h-7 text-[11px]" data-testid={`merge-b-${i}`}>
                        Keep {truncate(p.b.company_name, 18)} <ArrowRight className="h-3 w-3 mx-1"/> Archive {truncate(p.a.company_name, 14)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openRelated(key)} className="h-7 text-[11px] text-indigo-700" data-testid={`related-${i}`}>
                        Related
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openReason(key)} className="h-7 text-[11px] text-slate-600" data-testid={`not-dupe-${i}`}>
                        Not related
                      </Button>
                    </div>
                  </div>
                  {relatedOpen && (
                    <div className="mt-3 ml-1 p-3 rounded border border-indigo-200 bg-indigo-50" data-testid={`related-form-${i}`}>
                      <label className="text-[11px] font-medium text-slate-700">How are these related? (required — written to both records)</label>
                      <input
                        value={relText}
                        onChange={(e) => setRelText(e.target.value)}
                        placeholder={`e.g. ${p.b.company_name} is the Ghana arm of ${p.a.company_name}`}
                        className="mt-1 w-full text-sm rounded border border-indigo-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        data-testid={`related-rel-${i}`}
                      />
                      <textarea
                        value={relNote}
                        onChange={(e) => setRelNote(e.target.value)}
                        rows={2}
                        placeholder="Optional note for context…"
                        className="mt-2 w-full text-sm rounded border border-indigo-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        data-testid={`related-note-${i}`}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <Button size="sm" onClick={() => applyRelated(p, key)} disabled={submitting || !relText.trim()} className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" data-testid={`related-apply-${i}`}>
                          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Apply — related companies"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={relNotNow} className="h-7 text-[11px] text-slate-500" data-testid={`related-cancel-${i}`}>
                          Not now
                        </Button>
                      </div>
                    </div>
                  )}
                  {reasonOpen && (
                    <div className="mt-3 ml-1 p-3 rounded border border-slate-200 bg-slate-50" data-testid={`not-dupe-form-${i}`}>
                      <label className="text-[11px] font-medium text-slate-700">Why are these not related? (required — saved as a note on both)</label>
                      <textarea
                        value={reasonText}
                        onChange={(e) => setReasonText(e.target.value)}
                        rows={2}
                        placeholder={`e.g. ${p.a.company_name} and ${p.b.company_name} are separate legal entities with different registration.`}
                        className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        data-testid={`not-dupe-reason-${i}`}
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <Button size="sm" onClick={() => applyNotDuplicate(p, key)} disabled={submitting || !reasonText.trim()} className="h-7 text-[11px]" data-testid={`not-dupe-apply-${i}`}>
                          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Apply — not related"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={notNow} className="h-7 text-[11px] text-slate-500" data-testid={`not-now-${i}`}>
                          Not now
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>

        {/* Recent audit */}
        <Card className="p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <ChevronRight className="h-4 w-4 text-slate-600"/>
            <h2 className="font-semibold text-slate-900 text-sm">Recent client writes & merges</h2>
          </div>
          {audit.length === 0 && <p className="text-sm text-slate-500">No recent events.</p>}
          <ul className="divide-y divide-slate-100 text-xs">
            {audit.map((e, i) => (
              <li key={i} className="py-2 flex gap-3" data-testid={`audit-row-${i}`}>
                <span className="text-slate-400 whitespace-nowrap font-mono text-[10px]">{(e.at || "").slice(0, 19).replace("T", " ")}</span>
                <Badge variant="outline" className="text-[10px] h-5">{e.action}</Badge>
                <span className="text-slate-500 text-[10px]">{e.source || "—"}</span>
                <span className="text-slate-700 truncate flex-1">
                  {e.action === "merge" ? `Merged ${e.source_client_id?.slice(0, 8)} → ${e.client_id?.slice(0, 8)} · aliases: ${(e.aliases_added || []).join(", ") || "none"}` :
                   e.action === "engagement_refine" ? `LLM → ${e.patch?.engagement_status} (${e.patch?.engagement_score}) — ${e.patch?.rationale?.slice(0, 100) || ""}` :
                   `${e.reference || e.client_id?.slice(0, 8)} · ${Object.keys(e.patch || {}).join(", ")}`}
                </span>
                <span className="text-slate-400 text-[10px] truncate max-w-[160px]">{e.actor}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Part 6 — honest merge confirmation. */}
      <Dialog open={!!mergeConfirm} onOpenChange={(o) => { if (!o) setMergeConfirm(null); }}>
        <DialogContent data-testid="merge-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm merge</DialogTitle>
          </DialogHeader>
          {mergeConfirm && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                <span className="font-semibold text-emerald-700" data-testid="merge-keeper-name">{mergeConfirm.keeper.company_name}</span> will survive.
                <br/>
                <span className="font-semibold text-red-700" data-testid="merge-source-name">{mergeConfirm.source.company_name}</span> will be archived, its records rewritten onto the survivor, and its name kept as an alias.
              </p>
              {mergeConfirm.keeper.country && mergeConfirm.source.country && mergeConfirm.keeper.country !== mergeConfirm.source.country && (
                <div className="p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-[12px]" data-testid="merge-country-warning">
                  ⚑ These records hold different countries — {mergeConfirm.keeper.company_name} ({mergeConfirm.keeper.country}) and {mergeConfirm.source.company_name} ({mergeConfirm.source.country}). A differing country usually means a country arm, not a duplicate. Consider “Related” instead.
                </div>
              )}
              <p className="text-red-600 font-medium" data-testid="merge-irreversible">This cannot be undone.</p>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setMergeConfirm(null)} data-testid="merge-cancel-btn">Cancel</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={merging} onClick={doMerge} data-testid="merge-confirm-btn">
                  {merging ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Merge — cannot be undone"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const colour = {
    resolved:     "bg-emerald-100 text-emerald-800 border-emerald-300",
    ambiguous:    "bg-amber-100 text-amber-800 border-amber-300",
    not_found:    "bg-red-100 text-red-800 border-red-300",
    auto_created: "bg-sky-100 text-sky-800 border-sky-300",
  }[status] || "bg-slate-100 text-slate-700";
  return <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-semibold ${colour}`}>{status}</span>;
}

function truncate(s, n) { return s && s.length > n ? s.slice(0, n - 1) + "…" : s; }
